import { useEffect, type JSX } from 'react';
import { useLocation } from 'react-router';
import { INDEXABLE_ROBOTS, ORIGIN, metaFor } from './routeMeta';

/**
 * Writes the head from the route, because a single-page app has one head and
 * fourteen screens.
 *
 * It draws nothing. It sits inside the router and beside the routes so the tag
 * changes when the URL does, which is the whole point: a crawler that renders
 * JavaScript reads the head after the app has booted, and until now it read
 * the sales screen's head on every URL.
 *
 * `noindex` is asked for in two places on purpose. Here, for the crawler that
 * runs JavaScript, and in `public/_headers` as `X-Robots-Tag`, for the one
 * that does not. Neither covers the other: the header cannot see a client-side
 * route change, and this cannot be read without a renderer.
 */
export function DocumentMeta(): JSX.Element | null {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaFor(pathname);

    document.title = meta.title;
    setMeta('description', meta.description);
    setMeta('robots', meta.indexable ? INDEXABLE_ROBOTS : 'noindex, follow');
    // A canonical on a `noindex` page is two instructions that disagree. The
    // page that is allowed in the index is the only one that names itself.
    setCanonical(meta.indexable ? `${ORIGIN}${pathname === '/' ? '/' : pathname}` : undefined);
  }, [pathname]);

  return null;
}

/** Absent content removes the tag rather than leaving an empty one behind. */
function setMeta(name: string, content: string | undefined): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (!content) {
    existing?.remove();
    return;
  }

  const tag = existing ?? document.head.appendChild(document.createElement('meta'));
  tag.setAttribute('name', name);
  tag.setAttribute('content', content);
}

function setCanonical(href: string | undefined): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!href) {
    existing?.remove();
    return;
  }

  const tag = existing ?? document.head.appendChild(document.createElement('link'));
  tag.setAttribute('rel', 'canonical');
  tag.setAttribute('href', href);
}
